import type { SortDirection } from '@tanstack/react-table';
import { TableHeader } from '@vizit/atoms';
import * as React from 'react';

const ROLE_COLUMN_WIDTH = 'w-110 max-w-[220px]';
const SCOPE_COLUMN_WIDTH = 'w-[605px] max-w-[605px]';

interface UsersTableHeaderProps {
  /**
   * The field currently being sorted
   * @optional
   */
  sortBy?: string;
  /**
   * The current sort direction
   * @optional
   */
  sortDirection?: SortDirection | false;
  /**
   * Callback when a sortable column header is clicked
   * @optional
   */
  onSort?: (field: string) => void;
  /**
   * Whether to render the Role and Scope columns (hierarchical orgs only)
   * @optional
   */
  showRoleScope?: boolean;
}

/**
 * Memoized header component for the users table with sortable columns
 * for Email Address, Status, and Last Login fields.
 *
 * @param props - Sort state and callback props
 */
const UsersTableHeader = React.memo((props: UsersTableHeaderProps) => {
  const { sortBy, sortDirection, onSort, showRoleScope = false } = props;

  const getSortDirection = (field: string): SortDirection | false => {
    if (sortBy === field && sortDirection) return sortDirection;
    return false;
  };

  return (
    <TableHeader>
      <TableHeader.SortableColumn
        isRowHeader
        style={{ width: '25%' }}
        sortDirection={getSortDirection('email_address')}
        onSort={() => onSort?.('email_address')}
      >
        Email address
      </TableHeader.SortableColumn>
      {showRoleScope && <TableHeader.Column className={ROLE_COLUMN_WIDTH}>Role</TableHeader.Column>}
      {showRoleScope && <TableHeader.Column className={SCOPE_COLUMN_WIDTH}>Scope</TableHeader.Column>}
      <TableHeader.SortableColumn
        className="w-auto"
        sortDirection={getSortDirection('status')}
        onSort={() => onSort?.('status')}
      >
        Status
      </TableHeader.SortableColumn>
      <TableHeader.SortableColumn
        className="w-auto"
        sortDirection={getSortDirection('last_login_at')}
        onSort={() => onSort?.('last_login_at')}
      >
        Last login
      </TableHeader.SortableColumn>
      <TableHeader.Column aria-label="Actions" className="w-auto">
        {' '}
      </TableHeader.Column>
    </TableHeader>
  );
});

export { UsersTableHeader };
export type { UsersTableHeaderProps };
